import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { LegalFooter } from "../components/LegalFooter";
import { ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { RETURN_TO_KEY } from "../lib/api";

// After a mid-purchase session expiry, return to the subscription screen (never auto-purchase).
// Paywall clears the key on mount, so both redirect paths below agree regardless of order.
const takeReturnTo = () => (sessionStorage.getItem(RETURN_TO_KEY) === "/paywall" ? "/paywall" : "/");

export default function Login() {
  const { login, verifyMfa, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("password"); // "password" | "mfa"
  const [mfaToken, setMfaToken] = useState(null);
  const [code, setCode] = useState("");

  useEffect(() => {
    if (user) navigate(takeReturnTo(), { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email.trim().toLowerCase(), password);
    setLoading(false);
    if (res.ok) { toast.success("Welcome back"); navigate(takeReturnTo(), { replace: true }); }
    else if (res.mfaRequired) { setMfaToken(res.mfaToken); setStep("mfa"); setCode(""); }
    else toast.error(res.error);
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await verifyMfa(mfaToken, code.trim());
    setLoading(false);
    if (res.ok) {
      toast.success(res.usedBackup ? "Signed in with a backup code" : "Welcome back");
      navigate(takeReturnTo(), { replace: true });
    } else toast.error(res.error);
  };

  return (
    <div className="df-shell df-safe-top min-h-screen flex flex-col justify-center px-6">
      <div className="df-fade-in">
        <div className="flex items-center gap-3 mb-8">
          <img src="/molaroo-mark.png" alt="Molaroo" className="h-14 w-14 rounded-2xl object-contain bg-white shadow-sm" />
          <div>
            <div className="text-2xl font-heading font-bold tracking-tight text-slate-900 leading-none">Molaroo</div>
            <div className="text-sm text-slate-500 mt-1">Your income, organised.</div>
            <div className="text-xs text-slate-400 mt-0.5">by NavScora</div>
          </div>
        </div>

        {step === "password" ? (
          <>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900 mb-1">Welcome back</h1>
            <p className="text-slate-500 mb-8">Know what you've earned, received and what's still to come.</p>

            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label htmlFor="email" className="text-slate-700">Email</Label>
                <Input id="email" data-testid="login-email" type="email" value={email}
                  onChange={(e) => setEmail(e.target.value)} className="mt-1.5 h-12 rounded-xl bg-white" required />
              </div>
              <div>
                <Label htmlFor="password" className="text-slate-700">Password</Label>
                <Input id="password" data-testid="login-password" type="password" value={password}
                  onChange={(e) => setPassword(e.target.value)} className="mt-1.5 h-12 rounded-xl bg-white" required />
                <div className="text-right mt-1.5">
                  <Link to="/forgot-password" data-testid="login-forgot" className="text-sm text-[#0D786A] df-pressable">Forgot password?</Link>
                </div>
              </div>
              <Button type="submit" data-testid="login-submit" disabled={loading}
                className="w-full h-12 rounded-xl bg-[#0D786A] hover:bg-[#0A6055] text-base font-semibold df-pressable">
                {loading ? "Signing in…" : "Sign in"}
              </Button>
            </form>
            <p className="text-center text-sm text-slate-500 mt-6">
              New to Molaroo?{" "}
              <Link to="/signup" data-testid="login-to-signup" className="text-[#0D786A] font-semibold df-pressable">Create an account</Link>
            </p>
          </>
        ) : (
          <>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900 mb-1">Two-factor check</h1>
            <p className="text-slate-500 mb-8">Enter the 6-digit code from your authenticator app, or a backup code.</p>

            <form onSubmit={submitMfa} className="space-y-4">
              <div>
                <Label htmlFor="mfa-code" className="text-slate-700">Verification code</Label>
                <Input id="mfa-code" data-testid="login-mfa-code" inputMode="text" autoFocus value={code}
                  onChange={(e) => setCode(e.target.value)} placeholder="123456 or XXXX-XXXX"
                  className="mt-1.5 h-12 rounded-xl bg-white font-num tracking-widest" required />
              </div>
              <Button type="submit" data-testid="login-mfa-submit" disabled={loading}
                className="w-full h-12 rounded-xl bg-[#0D786A] hover:bg-[#0A6055] text-base font-semibold df-pressable">
                {loading ? "Verifying…" : "Verify & sign in"}
              </Button>
              <button type="button" data-testid="login-mfa-back" onClick={() => { setStep("password"); setCode(""); }}
                className="w-full text-sm text-slate-500 df-pressable">Back to sign in</button>
            </form>
          </>
        )}

        <div className="mt-8 flex items-start gap-2 text-xs text-slate-500 bg-white rounded-2xl p-4 border border-slate-200/70">
          <ShieldCheck size={16} className="text-[#0D786A] mt-0.5 shrink-0" />
          <span>Molaroo organises your financial records — it is not accounting, tax or legal advice.</span>
        </div>
        <LegalFooter className="mt-6" />
      </div>
    </div>
  );
}
